import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useTranslation, useBi } from '@wix/yoshi-flow-editor';
import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';

import { groupFeedTopicsSelectTopicInFeed } from '@wix/bi-logger-groups/v2';

import { selectAllTopics, selectFeedPermissionsByGroup } from 'store/selectors';
import { EFilterKeys } from '@wix/social-groups-serverless/dist/feed/constants';

import { useController } from 'common/context/controller';
import { useDidUpdate } from 'common/hooks';

import { TabItem, Tabs } from 'wui/Tabs';

import classes from './GroupTopicsFilter.scss';

export interface IGroupTopicsFilterProps {
  groupId: string;
}

const ALL_TOPICS_ID = 'all';

export function GroupTopicsFilter(props: IGroupTopicsFilterProps) {
  const { groupId } = props;

  const { t } = useTranslation();
  const bi = useBi();

  const { topics$ } = useController();
  const { params } = useCurrentStateAndParams();
  const router = useRouter();

  const topics = useSelector(selectAllTopics);
  const permissions = useSelector(selectFeedPermissionsByGroup(groupId));

  const activeId = params[EFilterKeys.TOPICS];

  useDidUpdate(() => {
    if (permissions.canViewPosts) {
      topics$.fetch(groupId);
    }
  }, [permissions.canViewPosts]);

  const topicItems: TabItem[] = useMemo(() => {
    return [
      {
        id: ALL_TOPICS_ID,
        title: t('groups-web.discussion.topics.filter.all'),
      },
      ...topics.map((topic) => ({
        id: topic.id,
        title: `${topic.displayName} (${topic.count})`,
      })),
    ];
  }, [topics]);

  if (!topics.length || !permissions.canViewPosts) {
    return null;
  }

  return (
    <Tabs
      aria-label={t('groups-web.discussion.topics.filter.aria-label')}
      value={activeId || ALL_TOPICS_ID}
      skin="clear"
      items={topicItems}
      onChange={handleTopicOnChange}
      className={classes.root}
      alignment="left"
      allowDeselect
    />
  );

  function handleTopicOnChange(id: string) {
    bi.report(
      groupFeedTopicsSelectTopicInFeed({
        groupId: groupId as string,
        origin: 'feed_topics_section',
      }),
    );

    router.go('group.discussion.feed', {
      [EFilterKeys.TOPICS]: id !== ALL_TOPICS_ID ? id : undefined,
      disableScrollToTop: true,
    });
  }
}

GroupTopicsFilter.displayName = 'GroupTopicsFilter';
